import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, LogIn, ArrowLeft, ShieldCheck } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useSite } from "@/context/SiteContext";
import { resolveImg } from "@/lib/api";
import { useSeo } from "@/hooks/useSeo";
import { IslamicPatternCorner } from "@/components/IslamicPattern";

const Login = () => {
  const { login, user } = useAuth();
  const { settings } = useSite();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  useSeo("Login Admin");

  useEffect(() => { if (user) navigate("/admin"); }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Login berhasil");
      navigate("/admin");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login gagal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-gradient-to-br from-emerald-800 via-emerald-900 to-emerald-950 px-4">
      <IslamicPatternCorner className="pointer-events-none absolute -left-20 -top-20 h-96 w-96 text-white/5" />
      <IslamicPatternCorner className="pointer-events-none absolute -bottom-24 -right-20 h-96 w-96 text-white/5" />
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="relative w-full max-w-md rounded-3xl bg-white p-8 shadow-2xl">
        <div className="flex flex-col items-center text-center">
          <img src={resolveImg(settings.logo_url)} alt="Logo MI Futuhiyyah" className="h-16 w-16 rounded-full object-contain ring-2 ring-emerald-100" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-slate-900">Login Administrator</h1>
          <p className="mt-1 text-sm text-slate-500">MI Futuhiyyah Doro - Pekalongan</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="text-sm font-medium text-slate-700">Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="email admin" data-testid="admin-login-email-input" className="mt-1.5" />
          </div>
          <div>
            <label className="text-sm font-medium text-slate-700">Kata Sandi</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" data-testid="admin-login-password-input" className="mt-1.5" />
          </div>
          <Button type="submit" disabled={loading} className="w-full gap-2 bg-emerald-700 hover:bg-emerald-800" data-testid="admin-login-submit-button">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
            {loading ? "Memproses..." : "Masuk"}
          </Button>
        </form>

        <div className="mt-4 flex items-center justify-center gap-1.5 rounded-lg bg-emerald-50 py-2 text-xs text-emerald-700">
          <ShieldCheck className="h-3.5 w-3.5" /> Area khusus admin madrasah
        </div>

        <Link to="/" className="mt-6 flex items-center justify-center gap-1.5 text-sm text-slate-500 hover:text-emerald-700" data-testid="login-back-home">
          <ArrowLeft className="h-4 w-4" /> Kembali ke Beranda
        </Link>
      </motion.div>
    </div>
  );
};

export default Login;
